import React from 'react';
import {
  Button,
  DropdownMenu,
  Icon,
  SocialMediaIcon,
  type SocialMediaIconProps,
} from '@kontentino/ui';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { useTranslation } from 'react-i18next';
import { AuthorizationPage } from 'app/modules/settings/types/authorizedAccount';
import PageTypeUtils from 'app/utils/pageType';

interface Props {
  pages: AuthorizationPage[];
  onReconnect: (page: AuthorizationPage) => void;
}

const CalendarDisconnectedProfilesBannerContent = ({
  pages,
  onReconnect,
}: Props) => {
  const { t } = useTranslation();

  const isSingle = pages.length === 1;
  const totalPostsAtRisk = pages.reduce(
    (total, page) => total + (page.postsAtRisk ?? 0),
    0,
  );

  return (
    <div
      className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-2 tw-gap-y-1"
      data-testid="calendar_disconnected-profiles-banner_row"
    >
      <span
        className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-1 tw-text-sm"
        data-testid="calendar_disconnected-profiles-banner_message"
      >
        <Icon icon={faTriangleExclamation} size="sm" />
        <span className="tw-font-semibold">{t('reconnectionRequired')}.</span>
        <span>
          {isSingle
            ? t('calendarDisconnectedProfilesBanner.message', {
                names: pages[0].pageName,
              })
            : t('calendarDisconnectedProfilesBanner.messageCount', {
                count: pages.length,
              })}
        </span>
        {isSingle && !!totalPostsAtRisk && (
          <span
            data-testid={`calendar_disconnected-profiles-banner_posts-at-risk-${pages[0].pageId}`}
          >
            {t('calendarDisconnectedProfilesBanner.postsAtRisk', {
              count: totalPostsAtRisk,
            })}
            .
          </span>
        )}
      </span>

      {isSingle ? (
        <Button
          variant="plain"
          size="small"
          onClick={() => onReconnect(pages[0])}
          className="!tw-h-auto tw-whitespace-nowrap !tw-px-0 !tw-py-0 !tw-text-sm !tw-font-semibold !tw-text-warning-140 !tw-underline hover:!tw-text-warning-160"
          data-name="calendar_disconnected-profiles-banner_reconnect-button"
          data-cy={`calendar_disconnected-profiles-banner_reconnect-button-${pages[0].pageId}`}
          data-testid={`calendar_disconnected-profiles-banner_reconnect-${pages[0].pageId}`}
        >
          {t('calendarDisconnectedProfilesBanner.reconnectYourProfile')}
        </Button>
      ) : (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className="tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-text-warning-140 tw-underline hover:tw-text-warning-160"
            data-name="calendar_disconnected-profiles-banner_toggle-button"
            data-cy="calendar_disconnected-profiles-banner_toggle-button"
            data-testid="calendar_disconnected-profiles-banner_toggle"
          >
            <span className="tw-flex tw-items-center tw-gap-1">
              {t('calendarDisconnectedProfilesBanner.profilesCount', {
                count: pages.length,
              })}
              <Icon icon={faChevronDown} size="sm" />
            </span>
          </DropdownMenu.Trigger>
          {/* Capped so a long list scrolls inside the menu instead of running off a phone screen. */}
          <DropdownMenu.Content
            align="center"
            sideOffset={6}
            className="tw-z-20 tw-max-h-[50vh] tw-w-[min(340px,90vw)] tw-overflow-y-auto tw-p-1 tw-text-left"
          >
            {pages.map((page) => (
              <DropdownMenu.Item
                key={page.pageId}
                // The row is a container for the reconnect action, not itself selectable.
                onSelect={(event) => event.preventDefault()}
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-grayscale-10 tw-px-2 tw-py-2 last:tw-border-0"
                data-testid={`calendar_disconnected-profiles-banner_profile-${page.pageId}`}
              >
                <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                  {!!PageTypeUtils.getIconNameFromName(page.type) && (
                    <SocialMediaIcon
                      size={20}
                      type={
                        PageTypeUtils.getIconNameFromName(
                          page.type,
                        ) as SocialMediaIconProps['type']
                      }
                      className="tw-shrink-0"
                    />
                  )}
                  <span className="tw-flex tw-min-w-0 tw-flex-col">
                    <span className="tw-truncate tw-text-sm tw-font-medium tw-text-grayscale-180">
                      {page.pageName}
                    </span>
                    {!!page.postsAtRisk && (
                      <span
                        className="tw-text-xs tw-text-grayscale-70"
                        data-testid={`calendar_disconnected-profiles-banner_posts-at-risk-${page.pageId}`}
                      >
                        {t('calendarDisconnectedProfilesBanner.postsAtRisk', {
                          count: page.postsAtRisk,
                        })}
                      </span>
                    )}
                  </span>
                </span>
                <Button
                  variant="tertiary"
                  size="small"
                  iconBefore={<Icon icon={faArrowsRotate} size="sm" />}
                  onClick={() => onReconnect(page)}
                  className="tw-shrink-0 tw-whitespace-nowrap"
                  data-name="calendar_disconnected-profiles-banner_reconnect-button"
                  data-cy={`calendar_disconnected-profiles-banner_reconnect-button-${page.pageId}`}
                  data-testid={`calendar_disconnected-profiles-banner_reconnect-${page.pageId}`}
                >
                  {t('reconnect')}
                </Button>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
    </div>
  );
};

export default CalendarDisconnectedProfilesBannerContent;
